
import React from "react";
import { i18nManager } from "../../../../i18n";
import { useSearchParams } from "react-router-dom";
import { Schemes, Subscriber, Utils } from "../../../../utils";
import { ConfirmDialog, Elements, SidedContainer, Siren } from "../../../shared";
import { ButtonManageRef, Classname, FormControl, FormGroup, ObjectHelper, Scheme, TextAreaInput } from "@rivtn/noseur";
import { ApiResponseData, BaseService, ConfigurationType, IPlatformConfiguration, PlatformConfigurationService, SanitizedResponse } from "../../../../services";
import { ConfigurationInputs, ConfigurationValue } from "./ConfigUtils";

function PlatformConfigurations() {
    const dialogErrorRef = React.useRef<HTMLDivElement>(null);
    const platformConfigurationService = PlatformConfigurationService.getInstance();
    const [searchParams, setSearchParams] = useSearchParams(window.location.search);
    const labels = i18nManager.Labels.dashboard.ploutous.configurations.platform_configs;
    const [activePlatformConfiguration, setActivePlatformConfiguration] = React.useState<IPlatformConfiguration>();
    const [platformConfigurations, setPlatformConfigurations] = React.useState<ApiResponseData<IPlatformConfiguration>>({});
    labels.dialogs.manage.form.find((f) => f.name === "type")!.options = i18nManager.Labels.dashboard.ploutous.configurations.type_options.slice(1);
    labels.dialogs.manage.form.find((f) => f.name === "status")!.options = i18nManager.Labels.dashboard.ploutous.configurations.status_options.slice(1);
    labels.sided_labels.dropdown_form!.find((f) => f.control.name === "type")!.control.options = i18nManager.Labels.dashboard.ploutous.configurations.type_options;
    labels.sided_labels.dropdown_form!.find((f) => f.control.name === "status")!.control.options = i18nManager.Labels.dashboard.ploutous.configurations.status_options;

    React.useEffect(() => managePlatformConfiguration(), []);

    return SidedContainer({
        searchParams,
        setSearchParams,
        labels: labels.sided_labels,
        data: platformConfigurations,
        onAction: managePlatformConfiguration,
        queryData: fetchPlatformConfigurations,
        activeData: (activePlatformConfiguration ? {
            data: activePlatformConfiguration,
            element: (<React.Fragment>
                <div className="header">
                    <i className="fa fa-times" onClick={() => {
                        setActivePlatformConfiguration(undefined);
                        Utils.updateSearchParams("active_data_id", undefined, setSearchParams);
                    }} />
                    <span className="name">{activePlatformConfiguration?.key}</span>
                    <span className="id" style={{ fontWeight: "bold" }}>{activePlatformConfiguration?.external_id}</span>
                    <span className="id">{activePlatformConfiguration?.type}</span>
                    <span className="desc">{activePlatformConfiguration?.description}</span>
                </div>
                <div className="middle actions" style={{ marginBottom: 0, borderBottom: "none", paddingBottom: 0 }}>
                    <span style={{ marginTop: 5 }}><b>{i18nManager.Labels.dashboard.ploutous.value}</b></span>
                    <ConfigurationValue type={activePlatformConfiguration.type} value={activePlatformConfiguration.value} />
                </div>
                <div className="middle actions" style={{ marginBottom: 0 }}>
                    <span style={{ marginTop: 5 }} onClick={() => managePlatformConfiguration(activePlatformConfiguration)}><b>{i18nManager.Labels.dashboard.ploutous.created_by}:</b> {activePlatformConfiguration?.created_by?.first_name} {activePlatformConfiguration?.created_by?.last_name} ({activePlatformConfiguration?.created_by?.email})</span>
                    <span style={{ marginTop: 5 }} onClick={() => managePlatformConfiguration(activePlatformConfiguration)}><b>{i18nManager.Labels.dashboard.ploutous.updated_by}:</b> {activePlatformConfiguration?.updated_by?.first_name} {activePlatformConfiguration?.updated_by?.last_name} ({activePlatformConfiguration?.updated_by?.email})</span>
                    <span style={{ marginTop: 5 }} onClick={() => managePlatformConfiguration(activePlatformConfiguration)}><b>{i18nManager.Labels.dashboard.ploutous.created_at}:</b> {Utils.formatToFineDate5(activePlatformConfiguration?.created_at)}</span>
                    <span style={{ marginTop: 5 }} onClick={() => managePlatformConfiguration(activePlatformConfiguration)}><b>{i18nManager.Labels.dashboard.ploutous.updated_at}:</b> {Utils.formatToFineDate5(activePlatformConfiguration?.updated_at)}</span>
                </div>
                <div className="actions" style={{ overflow: "auto" }}>
                    <span className="link" onClick={() => managePlatformConfiguration(activePlatformConfiguration)}>{i18nManager.Labels.dashboard.ploutous.edit}</span>
                    {activePlatformConfiguration?.status !== "ACTIVE" && (<span className="link" onClick={() => activateDeactivatePlatformConfiguration(activePlatformConfiguration, true)}>{i18nManager.Labels.dashboard.ploutous.activate}</span>)}
                    {activePlatformConfiguration?.status === "ACTIVE" && (<span className="link danger" onClick={() => activateDeactivatePlatformConfiguration(activePlatformConfiguration, false)}>{i18nManager.Labels.dashboard.ploutous.deactivate}</span>)}
                    {activePlatformConfiguration?.status !== "DELETED" && (<span className="link danger" onClick={() => deletePlatformConfiguration(activePlatformConfiguration)}>{i18nManager.Labels.dashboard.ploutous.delete}</span>)}
                </div>
            </React.Fragment>)
        } : undefined),
        dataTemplate: (platformConfiguration: IPlatformConfiguration) => {
            return (<div className={Classname.build("channel", (activePlatformConfiguration?.external_id === platformConfiguration.external_id ? "active" : null))} onClick={() => {
                setActivePlatformConfiguration(platformConfiguration);
                Utils.updateSearchParams("active_data_id", platformConfiguration.external_id, setSearchParams);
            }}>
                <div className="id" style={platformConfiguration.status === "DELETED" ? { textDecoration: "line-through" } : {}}>
                    <span className="name" style={{ marginRight: 5 }}>{platformConfiguration.key}</span> <span className="type">- {platformConfiguration.type}</span>
                </div>
                <span className="desc">{platformConfiguration.description}</span>
                <div className="ctrls">
                    <i className="fa fa-edit link gray" style={{ marginRight: 10 }} onClick={() => managePlatformConfiguration(platformConfiguration)} />
                    <i className={Classname.build("fa fa-trash link danger", (platformConfiguration.status === "DELETED" ? "noseur-disabled" : null))} style={{ marginRight: 10 }} onClick={() => deletePlatformConfiguration(platformConfiguration)} />
                </div>
            </div>);
        }
    });

    function fetchPlatformConfigurations() {
        const id = searchParams.get("active_data_id");
        platformConfigurationService.queryPlatformConfigurations(Utils.normalizeUrlParams(searchParams)).then(({ apiResponse }: SanitizedResponse<IPlatformConfiguration>) => {
            setPlatformConfigurations(apiResponse.data!);
            if (id) for (const item of apiResponse.data?.content!) {
                if (item.external_id === id) {
                    setActivePlatformConfiguration(item);
                    break;
                }
            }
        }).catch(BaseService.reportError).finally(() => Subscriber.report(Subscriber.KEYS.QUERY_REQUEST_PROMISE_COMPLETED));
    }

    function managePlatformConfiguration(_platformConfiguration: IPlatformConfiguration = {} as any) {
        let changeProps: (type: ConfigurationType, v?: any) => void;
        const platformConfiguration: IPlatformConfiguration = ObjectHelper.clone(_platformConfiguration);
        const basics = labels.dialogs.manage.basic[platformConfiguration.external_id ? "update" : "create"];
        const dialog = ConfirmDialog({
            ...basics,
            closable: true,
            rightStyle: { flex: 1.5 },
            confirmLabel: basics.confirm_label,
            style: { minWidth: 800, maxWidth: "80%" },
            content: (<div className="add-new-role" ref={(_) => {
                changeProps?.(platformConfiguration.type, platformConfiguration.value);
            }}>
                {Elements.buildConfirmDialogForm(labels.dialogs.manage.form, (name: string, value: string) => {
                    dialogErrorRef.current!.innerText = " ";
                    (platformConfiguration as any)[name] = value;
                    if (name === "type") {
                        changeProps?.(value as ConfigurationType, platformConfiguration.value);
                    }
                }, platformConfiguration, undefined, { marginTop: 0 })}
                <div ref={dialogErrorRef} className="error"> </div>
            </div>),
            rightContent: (<div style={{ display: "flex", flexDirection: "column", height: "100%", borderLeft: "1px solid #F0F0F0", marginTop: 30, marginLeft: 5, paddingLeft: 25 }}>
                <ConfigurationInputs type={ConfigurationType.TEXT} value={platformConfiguration.value} changeProps={(cb) => changeProps = cb} onNewValue={(v) => {
                    platformConfiguration.value = v;
                }} />
            </div>),
            onConfirm: (button?: ButtonManageRef) => {
                let errorMessage;
                dialogErrorRef.current!.innerText = " ";
                if (Object.keys(platformConfiguration).length < 5) {
                    errorMessage = labels.dialogs.manage.fill_required_fields;
                }
                if (errorMessage) {
                    dialogErrorRef.current!.innerText = errorMessage;
                    return false;
                }
                button?.setLoadingState(true);
                const method = (!!platformConfiguration.external_id ? platformConfigurationService.updatePlatformConfiguration : platformConfigurationService.createPlatformConfiguration).bind(platformConfigurationService);
                method(platformConfiguration)
                    .then(({ }: SanitizedResponse<IPlatformConfiguration>) => {
                        Siren.alert(basics.successful);
                        dialog.hide(fetchPlatformConfigurations);
                    }).catch(({ errors }: SanitizedResponse<any>) => {
                        dialogErrorRef.current!.innerText = errors?.join("\n") ?? "";
                    }).finally(() => {
                        button?.setLoadingState(false);
                    });
                return false;
            }
        });
        dialog.show();
    }

    function activateDeactivatePlatformConfiguration(platformConfiguration: IPlatformConfiguration, activate: boolean) {
        const dialogLabels = labels.dialogs[activate ? "activate" : "deactivate"];
        const dialog = ConfirmDialog({
            closable: true,
            desc: dialogLabels.desc,
            title: dialogLabels.title,
            confirmScheme: (activate ? Schemes.RIVTN_QUIRINUS : Scheme.DANGER),
            onConfirm: (button?: ButtonManageRef) => {
                button?.setLoadingState(true);
                platformConfigurationService.updatePlatformConfiguration({ external_id: platformConfiguration.external_id, status: (activate ? "ACTIVE" : "IN_ACTIVE") }).then(() => {
                    Siren.alert(dialogLabels.successful);
                    dialog.hide(() => fetchPlatformConfigurations());
                }).catch(BaseService.reportError).finally(() => button?.setLoadingState(false));
                return false;
            }
        });
        dialog.show();
    }

    function deletePlatformConfiguration(platformConfiguration: IPlatformConfiguration) {
        const dialog = ConfirmDialog({
            closable: true,
            confirmScheme: Scheme.DANGER,
            desc: labels.dialogs.delete.desc,
            title: labels.dialogs.delete.title,
            onConfirm: (button?: ButtonManageRef) => {
                button?.setLoadingState(true);
                platformConfigurationService.deletePlatformConfiguration(platformConfiguration.external_id).then(() => {
                    Siren.alert(labels.dialogs.delete.successful);
                    dialog.hide(() => setActivePlatformConfiguration(undefined));
                }).catch(BaseService.reportError).finally(() => button?.setLoadingState(false));
                return false;
            }
        });
        dialog.show();
    }

}

export default PlatformConfigurations;

